import { useState } from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { Badge, Box, Flex, Text, Tooltip, Button } from '@radix-ui/themes';

import { formatNumber } from '../../../lib/helpers';
import DigestEngagementByTile from './DigestEngagementByTile.jsx';
import styles from './TileDigest.module.css';

// Date when email open tracking feature went live
const EMAIL_OPENS_GO_LIVE_DATE = import.meta.env.VITE_EMAIL_OPENS_GO_LIVE_DATE
  ? new Date(import.meta.env.VITE_EMAIL_OPENS_GO_LIVE_DATE)
  : null;

const TileDigest = ({ digest }) => {
  const [showTiles, setShowTiles] = useState(false);
  const { t } = useTranslation('reports');

  const isEmailOpensDataAvailable = () => {
    if (!EMAIL_OPENS_GO_LIVE_DATE || !digest.tileEmailSentAt) return false;
    const [month, day, year] = digest.tileEmailSentAt.split('/').map(Number);
    const sentDate = new Date(2000 + year, month - 1, day);
    return sentDate >= EMAIL_OPENS_GO_LIVE_DATE;
  };

  const renderStatus = delivered => {
    const color = delivered ? 'green' : 'blue';
    const msg = delivered ? t('tileDigests.sent') : t('tileDigests.scheduled');

    return (
      <Badge
        color={color}
        variant="soft"
        size="1"
        className={styles.statusBadge}
      >
        {msg}
      </Badge>
    );
  };

  const getMainUniqueClicks = () => {
    return digest.loginsBySubjectLine[digest.subject] || 0;
  };

  const getMainTotalClicks = () => {
    return digest.totalLoginsBySubjectLine[digest.subject] || 0;
  };

  const getFollowUpUniqueClicks = () => {
    if (!digest.followUpEmail) return 0;
    return digest.loginsBySubjectLine[digest.followUpEmail.subject] || 0;
  };

  const getFollowUpTotalClicks = () => {
    if (!digest.followUpEmail) return 0;
    return digest.totalLoginsBySubjectLine[digest.followUpEmail.subject] || 0;
  };

  const sentAt = `${digest.tileEmailSentAt}, ${digest.tileEmailSentAtFormatted}`;

  return (
    <Box className={styles.wrapper}>
      <Flex direction="column" gap="2">
        <Flex justify="between" align="center" gap="3">
          <Text size="3" weight="bold" data-testid="digest-subject">
            {digest.subject}
          </Text>
          <Text size="2" color="gray" data-testid="digest-sent-at">
            {sentAt}
          </Text>
        </Flex>

        <Flex justify="between" align="center" gap="4" wrap="wrap">
          <Flex align="center" gap="4" wrap="wrap">
            {renderStatus(digest.delivered)}
            <Box className={styles.divider} />
            <Flex align="center" gap="1">
              <Text size="2" color="gray">
                {t('tileDigests.recipients')}
              </Text>
              <Text
                size="2"
                weight="medium"
                data-testid="digest-recipient-count"
              >
                {formatNumber(digest.recipientCount)}
              </Text>
            </Flex>
            {digest.followUpEmail && (
              <>
                <Box className={styles.divider} />
                <Flex align="center" gap="1">
                  <Text size="2" color="gray">
                    {t('tileDigests.followUp')}
                  </Text>
                  <Text size="2" weight="medium">
                    {digest.followUpEmail.sendOn}
                  </Text>
                </Flex>
              </>
            )}
          </Flex>
        </Flex>
      </Flex>

      <Box className={styles.statsSection}>
        <Flex gap="3">
          <Tooltip
            content={
              isEmailOpensDataAvailable()
                ? t('tileDigests.totalEmailOpensTooltip')
                : t('tileDigests.emailOpensNotAvailable')
            }
          >
            <Box
              className={
                isEmailOpensDataAvailable()
                  ? styles.statCard
                  : styles.statCardDisabled
              }
            >
              <Text
                size="7"
                weight="bold"
                className={styles.statValue}
                data-testid="digest-total-email-opens"
              >
                {isEmailOpensDataAvailable()
                  ? formatNumber(digest.totalEmailOpens)
                  : '-'}
              </Text>
              <Text size="1" weight="medium" color="gray">
                {t('tileDigests.totalEmailOpens')}
              </Text>
            </Box>
          </Tooltip>

          <Tooltip
            content={
              isEmailOpensDataAvailable()
                ? t('tileDigests.uniqueEmailOpensTooltip')
                : t('tileDigests.emailOpensNotAvailable')
            }
          >
            <Box
              className={
                isEmailOpensDataAvailable()
                  ? styles.statCard
                  : styles.statCardDisabled
              }
            >
              <Text
                size="7"
                weight="bold"
                className={styles.statValue}
                data-testid="digest-unique-email-opens"
              >
                {isEmailOpensDataAvailable()
                  ? formatNumber(digest.uniqueEmailOpens)
                  : '-'}
              </Text>
              <Text size="1" weight="medium" color="gray">
                {t('tileDigests.uniqueEmailOpens')}
              </Text>
            </Box>
          </Tooltip>

          <Tooltip
            content={
              <Trans
                i18nKey="tileDigests.uniqueVisitsTooltip"
                ns="reports"
                components={{ em: <em /> }}
              />
            }
          >
            <Box className={styles.statCard}>
              <Text
                size="7"
                weight="bold"
                className={styles.statValue}
                data-testid="digest-unique-visits"
              >
                {formatNumber(getMainUniqueClicks())}
              </Text>
              <Text size="1" weight="medium" color="gray">
                {t('tileDigests.uniqueClicks')}
              </Text>
            </Box>
          </Tooltip>

          <Tooltip
            content={
              <Trans
                i18nKey="tileDigests.totalVisitsTooltip"
                ns="reports"
                components={{ em: <em /> }}
              />
            }
          >
            <Box className={styles.statCard}>
              <Text
                size="7"
                weight="bold"
                className={styles.statValue}
                data-testid="digest-total-visits"
              >
                {formatNumber(getMainTotalClicks())}
              </Text>
              <Text size="1" weight="medium" color="gray">
                {t('tileDigests.totalClicks')}
              </Text>
            </Box>
          </Tooltip>
        </Flex>
      </Box>

      {digest.followUpEmail && (
        <Box className={styles.followUpSection}>
          <Flex direction="column" gap="2" mb="3">
            <Flex justify="between" align="center" gap="3">
              <Text size="3" weight="bold">
                {digest.followUpEmail.subject}
              </Text>
              <Text size="2" color="gray">
                {digest.followUpEmail.sendOn},{' '}
                {digest.followUpEmail.sendOnFormatted}
              </Text>
            </Flex>

            <Flex align="center" gap="4" wrap="wrap">
              {renderStatus(digest.followUpStatus)}
              <Box className={styles.divider} />
              <Flex align="center" gap="1">
                <Text size="2" color="gray">
                  {t('tileDigests.recipients')}
                </Text>
                <Text size="2" weight="medium">
                  {formatNumber(digest.followUpEmail.recipientCount)}
                </Text>
              </Flex>
            </Flex>
          </Flex>

          <Flex gap="3">
            <Tooltip
              content={
                isEmailOpensDataAvailable()
                  ? t('tileDigests.totalEmailOpensTooltip')
                  : t('tileDigests.emailOpensNotAvailable')
              }
            >
              <Box
                className={
                  isEmailOpensDataAvailable()
                    ? styles.statCard
                    : styles.statCardDisabled
                }
              >
                <Text size="7" weight="bold" className={styles.statValue}>
                  {isEmailOpensDataAvailable()
                    ? formatNumber(digest.followUpEmail.totalEmailOpens)
                    : '-'}
                </Text>
                <Text size="1" weight="medium" color="gray">
                  {t('tileDigests.totalEmailOpens')}
                </Text>
              </Box>
            </Tooltip>

            <Tooltip
              content={
                isEmailOpensDataAvailable()
                  ? t('tileDigests.uniqueEmailOpensTooltip')
                  : t('tileDigests.emailOpensNotAvailable')
              }
            >
              <Box
                className={
                  isEmailOpensDataAvailable()
                    ? styles.statCard
                    : styles.statCardDisabled
                }
              >
                <Text size="7" weight="bold" className={styles.statValue}>
                  {isEmailOpensDataAvailable()
                    ? formatNumber(digest.followUpEmail.uniqueEmailOpens)
                    : '-'}
                </Text>
                <Text size="1" weight="medium" color="gray">
                  {t('tileDigests.uniqueEmailOpens')}
                </Text>
              </Box>
            </Tooltip>

            <Tooltip
              content={
                <Trans
                  i18nKey="tileDigests.uniqueVisitsTooltip"
                  ns="reports"
                  components={{ em: <em /> }}
                />
              }
            >
              <Box className={styles.statCard}>
                <Text size="7" weight="bold" className={styles.statValue}>
                  {formatNumber(getFollowUpUniqueClicks())}
                </Text>
                <Text size="1" weight="medium" color="gray">
                  {t('tileDigests.uniqueClicks')}
                </Text>
              </Box>
            </Tooltip>

            <Tooltip
              content={
                <Trans
                  i18nKey="tileDigests.totalVisitsTooltip"
                  ns="reports"
                  components={{ em: <em /> }}
                />
              }
            >
              <Box className={styles.statCard}>
                <Text size="7" weight="bold" className={styles.statValue}>
                  {formatNumber(getFollowUpTotalClicks())}
                </Text>
                <Text size="1" weight="medium" color="gray">
                  {t('tileDigests.totalClicks')}
                </Text>
              </Box>
            </Tooltip>
          </Flex>
        </Box>
      )}

      <Box mt="3">
        <Button
          variant="ghost"
          size="2"
          onClick={() => setShowTiles(!showTiles)}
          className={styles.expandButton}
          data-testid="digest-tiles"
        >
          <i
            className={`fa-solid fa-chevron-down ${styles.chevron} ${
              showTiles ? styles.chevronUp : ''
            }`}
          />
          <Text size="2" weight="bold">
            {t('tileDigests.viewTiles')}
          </Text>
        </Button>
      </Box>

      {showTiles && (
        <Box className={styles.tilesSection}>
          <DigestEngagementByTile digestId={digest.tileEmailId} />
        </Box>
      )}
    </Box>
  );
};

TileDigest.propTypes = {
  digest: PropTypes.shape({
    tileEmailId: PropTypes.string.isRequired,
    tileEmailSentAt: PropTypes.string,
    tileEmailSentAtFormatted: PropTypes.string,
    delivered: PropTypes.bool,
    recipientCount: PropTypes.number,
    subject: PropTypes.string.isRequired,
    altSubject: PropTypes.string,
    loginsBySubjectLine: PropTypes.object.isRequired,
    totalLoginsBySubjectLine: PropTypes.object.isRequired,
    loginsFromEmail: PropTypes.number,
    totalLoginsFromEmail: PropTypes.number,
    totalEmailOpens: PropTypes.number,
    uniqueEmailOpens: PropTypes.number,
    followUpStatus: PropTypes.bool,
    followUpEmail: PropTypes.shape({
      sendOn: PropTypes.string.isRequired,
      sendOnFormatted: PropTypes.string.isRequired,
      subject: PropTypes.string.isRequired,
      recipientCount: PropTypes.number.isRequired,
      totalEmailOpens: PropTypes.number.isRequired,
      uniqueEmailOpens: PropTypes.number.isRequired,
    }),
  }).isRequired,
};

export default TileDigest;
